import Dropdown from 'bootstrap/js/dist/dropdown';
import debounce from 'lodash.debounce';
import memoize from 'lodash.memoize';
import escapeRegExp from 'lodash.escaperegexp';
import {
	clearUserSearchHistory,
	getSearchbox,
	getUserContentHistory,
	getUserSearchHistory,
	SearchResult,
} from '../../lib/api.ts';
import { createMenuRenderer } from './renderer.ts';
import {
	addHistoryItemToLocalStorage,
	getHistoryListFromLocalStorage,
	removeHistoryItemsFromLocalStorage,
	STORAGE_LIMIT,
} from './storage.ts';
import { EVENT_DROPDOWN_SHOW } from '../../../shared/lib/bootstrap.ts';
import { logException } from '../../../shared/lib/logging.ts';
import { normalizeSerbianLettersForRegex } from '../../../shared/lib/regex.ts';

const DEFAULT_TEXT_SEARCH_FOR = 'Search for';
const DEFAULT_TEXT_PRODUCTS_HEADER = 'Products';
const DEFAULT_TEXT_CATEGORIES_HEADER = 'Categories';
const DEFAULT_TEXT_BRANDS_HEADER = 'Brands';
const DEFAULT_TEXT_NO_RESULTS = 'No results for';
const DEFAULT_TEXT_HISTORY_HEADER = 'Search history';
const DEFAULT_TEXT_CONTENT_HEADER = 'Recently viewed products';

const DEFAULT_IS_HISTORY_ENABLED = false;

const ATTRIBUTE_SEARCH = 'data-cs-header-search';
const ATTRIBUTE_SEARCH_SEARCH_FOR_TEXT = `${ATTRIBUTE_SEARCH}-results-text`;
const ATTRIBUTE_SEARCH_PRODUCTS_HEADER_TEXT = `${ATTRIBUTE_SEARCH}-products-text`;
const ATTRIBUTE_SEARCH_CATEGORIES_HEADER_TEXT = `${ATTRIBUTE_SEARCH}-categories-text`;
const ATTRIBUTE_SEARCH_BRANDS_HEADER_TEXT = `${ATTRIBUTE_SEARCH}-brands-text`;
const ATTRIBUTE_SEARCH_NO_RESULTS_TEXT = `${ATTRIBUTE_SEARCH}-no-results-text`;
const ATTRIBUTE_SEARCH_HISTORY_HEADER_TEXT = `${ATTRIBUTE_SEARCH}-history-text`;
const ATTRIBUTE_SEARCH_CONTENT_HEADER_TEXT = `${ATTRIBUTE_SEARCH}-content-text`;

const ATTRIBUTE_SEARCH_IS_HISTORY_ENABLED = `${ATTRIBUTE_SEARCH}-is-history-enabled`;

const SELECTOR_INPUT_SEARCH = '.header-search__input';
const SELECTOR_BUTTON_SEARCH = '.header-search__submit';
const SELECTOR_DROPDOWN_MENU = '.header-search__dropdown';

const EVENT_DELAY_INPUT = 250; // ms

const INPUT_MIN_LENGTH = 2;
const INPUT_MAX_LENGTH = 100;

const PRODUCTS_LIMIT = 5;
const CATEGORIES_LIMIT = 3;
const BRANDS_LIMIT = 3;
const SEARCH_HISTORY_LIMIT = STORAGE_LIMIT;
const SEARCH_CONTENT_LIMIT = 5;

interface Collection {
	results: SearchResult[];
	$items: Array<HTMLLIElement | null>;
	$itemHeader: HTMLLIElement | null;
	limit: number;
	headerText: string;
}

const createEmptyCollection = (limit: number, headerText: string): Collection => {
	return {
		results: [],
		$items: [],
		$itemHeader: null,
		limit: limit,
		headerText: headerText,
	};
};

export default function loadHeaderSearch($forms: NodeListOf<HTMLFormElement>) {
	$forms.forEach(($form) => {
		const $input = $form.querySelector<HTMLInputElement>(SELECTOR_INPUT_SEARCH);
		if (!$input) return;

		const $button = $form.querySelector<HTMLButtonElement>(SELECTOR_BUTTON_SEARCH);
		if (!$button) return;

		const $menu = $form.querySelector<HTMLUListElement>(SELECTOR_DROPDOWN_MENU);
		if (!$menu) return;

		const $dropdown = Dropdown.getOrCreateInstance($input);
		const {
			renderSearchFor,
			renderItemsHeader,
			renderItem,
			renderNoResults,
			renderHistoryItemsHeader,
			renderContentItemsHeader,
			renderHistorySearchItem,
			renderHistoryContentItem,
			removeListItem,
			removeListItems,
		} = createMenuRenderer($menu);

		const searchForText = $form.getAttribute(ATTRIBUTE_SEARCH_SEARCH_FOR_TEXT) ?? DEFAULT_TEXT_SEARCH_FOR;
		const productsHeaderText =
			$form.getAttribute(ATTRIBUTE_SEARCH_PRODUCTS_HEADER_TEXT) ?? DEFAULT_TEXT_PRODUCTS_HEADER;
		const categoriesHeaderText =
			$form.getAttribute(ATTRIBUTE_SEARCH_CATEGORIES_HEADER_TEXT) ?? DEFAULT_TEXT_CATEGORIES_HEADER;
		const brandsHeaderText = $form.getAttribute(ATTRIBUTE_SEARCH_BRANDS_HEADER_TEXT) ?? DEFAULT_TEXT_BRANDS_HEADER;
		const noResultsText = $form.getAttribute(ATTRIBUTE_SEARCH_NO_RESULTS_TEXT) ?? DEFAULT_TEXT_NO_RESULTS;
		const historyHeaderText = $form.getAttribute(ATTRIBUTE_SEARCH_HISTORY_HEADER_TEXT) ?? DEFAULT_TEXT_HISTORY_HEADER;
		const contentHeaderText = $form.getAttribute(ATTRIBUTE_SEARCH_CONTENT_HEADER_TEXT) ?? DEFAULT_TEXT_CONTENT_HEADER;

		const useHistoryAPI = $form.hasAttribute(ATTRIBUTE_SEARCH_IS_HISTORY_ENABLED) ?? DEFAULT_IS_HISTORY_ENABLED;

		const collections: {
			product: Collection;
			category: Collection;
			brand: Collection;
		} = {
			product: createEmptyCollection(PRODUCTS_LIMIT, productsHeaderText),
			category: createEmptyCollection(CATEGORIES_LIMIT, categoriesHeaderText),
			brand: createEmptyCollection(BRANDS_LIMIT, brandsHeaderText),
		};

		let $itemSearchFor: HTMLLIElement | null;
		let $itemNoResults: HTMLLIElement | null;
		let $itemHistoryHeader: HTMLLIElement | null;
		let $itemsHistory: Array<HTMLLIElement | null> = [];
		let $itemContentHeader: HTMLLIElement | null;
		let $itemsContent: Array<HTMLLIElement | null> = [];

		const memoizedFetchSearchbox = memoize(getSearchbox, (data) => data.query);

		const isInputValid = () => {
			const searchText = $input.value.trim();
			const searchTextLength = searchText.length;

			return searchTextLength >= INPUT_MIN_LENGTH;
		};

		const clearHistory = () => {
			if ($itemHistoryHeader) $itemHistoryHeader = removeListItem($itemHistoryHeader);
			$itemsHistory = removeListItems($itemsHistory);
			if ($itemContentHeader) $itemContentHeader = removeListItem($itemContentHeader);
			$itemsContent = removeListItems($itemsContent);
		};

		const clearSearchItems = () => {
			if ($itemSearchFor) $itemSearchFor = removeListItem($itemSearchFor);
			Object.values(collections).forEach((collectionItem) => {
				if (collectionItem.$itemHeader) collectionItem.$itemHeader = removeListItem(collectionItem.$itemHeader);
				collectionItem.$items = removeListItems(collectionItem.$items);
			});
			if ($itemNoResults) $itemNoResults = removeListItem($itemNoResults);
		};

		const createHistoryList = () => {
			if (!$itemsHistory.length) {
				getUserSearchHistory()
					.then(({ data: { results: searchHistory } }) => {
						if (searchHistory.length) {
							clearSearchItems();
							clearHistory();

							$itemHistoryHeader = renderHistoryItemsHeader(historyHeaderText, () => {
								if (useHistoryAPI) {
									clearUserSearchHistory().catch((e) => {
										logException(e);
									});
								} else {
									removeHistoryItemsFromLocalStorage();
								}

								if ($itemHistoryHeader) $itemHistoryHeader = removeListItem($itemHistoryHeader);
								$itemsHistory = removeListItems($itemsHistory);
							});

							for (let i = 0; i < Math.min(SEARCH_HISTORY_LIMIT, searchHistory.length); i++) {
								$itemsHistory[i] = renderHistorySearchItem(searchHistory[i].search_term, $form.action);
							}
						}

						if (!$itemsContent.length) {
							getUserContentHistory()
								.then(({ data: { results: productHistory } }) => {
									if (productHistory.length) {
										$itemContentHeader = renderContentItemsHeader(contentHeaderText, () => {
											if ($itemContentHeader) $itemContentHeader = removeListItem($itemContentHeader);
											$itemsContent = removeListItems($itemsContent);
										});

										for (let i = 0; i < Math.min(SEARCH_CONTENT_LIMIT, productHistory.length); i++) {
											$itemsContent[i] = renderHistoryContentItem(
												productHistory[i].name,
												productHistory[i].get_absolute_url,
												productHistory[i].search_image_preview_url,
											);
										}
									}

									$dropdown.show();
								})
								.catch((e) => {
									logException(e);
								});
						}
					})
					.catch((e) => {
						logException(e);
					});
			}
		};

		const createSearchItems = () => {
			const trimmedInput = $input.value.trim();
			const searchText = trimmedInput.length > INPUT_MAX_LENGTH ? trimmedInput.substring(0, 100) : trimmedInput;
			const searchTextKeywords = searchText.split(' ').filter((keyword: string) => keyword);
			const searchTextRegex = new RegExp(
				`(${searchTextKeywords.map((keyword: string) => normalizeSerbianLettersForRegex(escapeRegExp(keyword))).join('|')})`,
				'gi',
			);
			const isInputLengthValid = isInputValid();

			if (isInputLengthValid) {
				memoizedFetchSearchbox({ query: searchText })
					.then(
						({
							data: {
								results: { brand: brands, category: categories, product: products },
							},
						}) => {
							clearHistory();
							clearSearchItems();

							collections.product.results = products;
							collections.category.results = categories;
							collections.brand.results = brands;

							if (Object.values(collections).some(({ results }) => results.length > 0)) {
								$itemSearchFor = renderSearchFor(searchForText, searchText);
							}

							Object.values(collections).forEach((collectionItem) => {
								if (collectionItem.results.length) {
									collectionItem.$itemHeader = renderItemsHeader(collectionItem.headerText);

									for (let i = 0; i < Math.min(collectionItem.limit, collectionItem.results.length); i++) {
										collectionItem.$items[i] = renderItem(
											searchTextRegex,
											collectionItem.results[i].name,
											collectionItem.results[i].get_absolute_url,
											collectionItem.results[i].search_image_preview_url,
										);
									}
								}
							});

							if (Object.values(collections).every(({ results }) => results.length === 0)) {
								$itemNoResults = renderNoResults(noResultsText, searchText);
							}

							$dropdown.show();
						},
					)
					.catch((e) => {
						logException(e);
					});
			} else {
				clearHistory();
				clearSearchItems();
			}
		};

		const createHistoryListFromLocalStorage = () => {
			if ($itemsHistory.length) return;

			const historyList = getHistoryListFromLocalStorage();
			if (!historyList.length) return;

			clearSearchItems();
			clearHistory();

			$itemHistoryHeader = renderHistoryItemsHeader(historyHeaderText, () => {
				if (useHistoryAPI) {
					clearUserSearchHistory().catch((e) => {
						logException(e);
					});
				} else {
					removeHistoryItemsFromLocalStorage();
				}

				if ($itemHistoryHeader) $itemHistoryHeader = removeListItem($itemHistoryHeader);
				$itemsHistory = removeListItems($itemsHistory);
			});

			for (let i = 0; i < Math.min(SEARCH_HISTORY_LIMIT, historyList.length); i++) {
				$itemsHistory[i] = renderHistorySearchItem(historyList[i], $form.action);
			}

			$dropdown.show();
		};

		const initializeMenu = () => {
			const isInputLengthValid = isInputValid();

			if (isInputLengthValid) {
				createSearchItems();
			} else if (useHistoryAPI) {
				createHistoryList();
			} else {
				createHistoryListFromLocalStorage();
			}
		};

		$input.addEventListener(EVENT_DROPDOWN_SHOW, () => initializeMenu());

		$input.addEventListener(
			'input',
			debounce(() => initializeMenu(), EVENT_DELAY_INPUT),
		);

		$form.addEventListener('submit', (event) => {
			if (!isInputValid()) {
				event.preventDefault();

				$input.focus();

				return;
			}

			const input = $input.value.trim();

			$input.value = input;

			if (!useHistoryAPI) {
				addHistoryItemToLocalStorage(input);
			}
		});
	});
}
